import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * ErrorBoundary - root-level crash guard.
 *
 * Phase 1 logs to console only. PROD-573 wires Application Insights via the
 * `componentDidCatch` hook; the seam is intentionally left here so the
 * telemetry adapter can plug in without touching component bodies.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // PROD-573 plug point: appInsights.trackException({ exception: error, properties: { stack: info.componentStack } });
    console.error('Pathway Studio crashed:', error, info);
  }

  reset = () => this.setState({ hasError: false, error: null });

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div
          role="alert"
          className="flex min-h-screen flex-col items-center justify-center bg-surface-base p-gutter-xl text-center"
        >
          <h1 className="text-signal-2xl font-semibold text-text-primary">
            Something went wrong
          </h1>
          <p className="mt-gutter-md max-w-md text-signal-base text-text-secondary">
            {this.state.error?.message ?? 'Unknown error'}
          </p>
          <button
            type="button"
            onClick={this.reset}
            className="mt-gutter-lg rounded-signal-md bg-signal-primary-600 px-gutter-lg py-gutter-sm text-signal-sm font-semibold text-white"
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
